import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  LogOut,
  Mail,
  Phone,
  Calendar,
  DollarSign,
  Loader2,
  RefreshCw,
  Inbox,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Search,
  ChevronDown,
  Pencil,
} from "lucide-react";
import Logo from "@/components/Logo";

const SITE_TYPE_LABELS = { landing: "Landing Page", multi: "Multi-página" };
const FEATURE_LABELS = {
  whatsapp: "WhatsApp",
  calendar: "Calendario",
  catalog: "Catálogo",
  blog: "Blog SEO",
  maps: "Maps",
};

const STATUSES = [
  { id: "new", label: "Nuevo", color: "slate" },
  { id: "contacted", label: "Contactado", color: "sky" },
  { id: "won", label: "Ganado", color: "emerald" },
  { id: "lost", label: "Perdido", color: "rose" },
];

const STATUS_STYLES = {
  new: "bg-slate-800 text-slate-300 border-slate-700",
  contacted: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  won: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  lost: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

const formatMXN = (n) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0,
  }).format(n);

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function AdminDashboard() {
  const { user, logout, authedAxios } = useAuth();
  const [tab, setTab] = useState("quotes");
  const [quotes, setQuotes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const api = authedAxios();
      const [q, b, m] = await Promise.all([api.get("/quotes"), api.get("/bookings"), api.get("/messages")]);
      setQuotes(q.data);
      setBookings(b.data);
      setMessages(m.data);
    } catch (err) {
      toast.error("No se pudieron cargar los leads");
      if (err?.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateQuoteStatus = async (id, newStatus) => {
    setQuotes((prev) => prev.map((q) => (q.id === id ? { ...q, status: newStatus } : q)));
    try {
      await authedAxios().patch(`/quotes/${id}`, { status: newStatus });
      toast.success(`Cotización marcada como ${STATUSES.find((s) => s.id === newStatus).label.toLowerCase()}`);
    } catch (err) {
      toast.error("No se pudo actualizar el estado");
      load();
    }
  };

  const updateBookingStatus = async (id, newStatus) => {
    setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status: newStatus } : b)));
    try {
      await authedAxios().patch(`/bookings/${id}`, { status: newStatus });
      toast.success(`Auditoría marcada como ${STATUSES.find((s) => s.id === newStatus).label.toLowerCase()}`);
    } catch (err) {
      toast.error("No se pudo actualizar el estado");
      load();
    }
  };

  const updateMessageStatus = async (id, newStatus) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, status: newStatus } : m)));
    try {
      await authedAxios().patch(`/messages/${id}`, { status: newStatus });
      toast.success(`Mensaje marcado como ${STATUSES.find((s) => s.id === newStatus).label.toLowerCase()}`);
    } catch (err) {
      toast.error("No se pudo actualizar el estado");
      load();
    }
  };

  const filteredMessages = useMemo(() => {
    const s = search.trim().toLowerCase();
    return messages.filter((m) => {
      if (statusFilter !== "all" && (m.status || "new") !== statusFilter) return false;
      if (!s) return true;
      return [m.name, m.email, m.company].some((v) => (v || "").toLowerCase().includes(s));
    });
  }, [messages, statusFilter, search]);

  const filteredQuotes = useMemo(() => {
    const s = search.trim().toLowerCase();
    return quotes.filter((q) => {
      if (statusFilter !== "all" && (q.status || "new") !== statusFilter) return false;
      if (!s) return true;
      return (
        q.email?.toLowerCase().includes(s) ||
        (q.company || "").toLowerCase().includes(s)
      );
    });
  }, [quotes, statusFilter, search]);

  const filteredBookings = useMemo(() => {
    const s = search.trim().toLowerCase();
    return bookings.filter((b) => {
      if (statusFilter !== "all" && (b.status || "new") !== statusFilter) return false;
      if (!s) return true;
      return (
        b.name?.toLowerCase().includes(s) ||
        b.phone?.toLowerCase().includes(s)
      );
    });
  }, [bookings, statusFilter, search]);

  const stats = useMemo(() => {
    const wonRevenue = quotes
      .filter((q) => q.status === "won")
      .reduce((s, q) => s + (q.total || 0), 0);
    const openPipeline = quotes
      .filter((q) => !q.status || q.status === "new" || q.status === "contacted")
      .reduce((s, q) => s + (q.total || 0), 0);
    return {
      totalQuotes: quotes.length,
      totalBookings: bookings.length,
      openPipeline,
      wonRevenue,
    };
  }, [quotes, bookings]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200" data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-slate-950/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-3">
            <Logo size="md" />
            <span className="hidden sm:inline-block px-2 py-1 text-[10px] font-mono uppercase rounded bg-orange-500/10 text-orange-400 border border-orange-500/30">
              admin
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/admin/contenido"
              data-testid="admin-content-link"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors"
            >
              <Pencil size={13} /> Editar contenido
            </Link>
            <Link
              to="/"
              data-testid="admin-back-to-site"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-sky-400"
            >
              Ver sitio <ExternalLink size={12} />
            </Link>
            <span className="hidden md:inline font-mono text-xs text-slate-500">
              {user?.email}
            </span>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-300 hover:text-white bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-lg"
            >
              <LogOut size={14} /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Title row */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="font-mono text-xs text-sky-400">// panel.leads</p>
            <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight text-white">
              Hola, {user?.name || "Isai"}.
            </h1>
            <p className="text-slate-400 mt-1">
              Aquí están tus leads y reservas en tiempo real.
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="admin-refresh"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-sky-300 bg-slate-900 hover:bg-slate-800 border border-slate-800 hover:border-sky-500/40 rounded-lg disabled:opacity-60"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />{" "}
            Actualizar
          </button>
        </div>

        {/* Stats */}
        <section className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            testId="stat-quotes"
            label="Cotizaciones"
            value={stats.totalQuotes}
            icon={<Inbox className="text-sky-400" size={18} />}
          />
          <StatCard
            testId="stat-bookings"
            label="Auditorías"
            value={stats.totalBookings}
            icon={<Calendar className="text-orange-400" size={18} />}
          />
          <StatCard
            testId="stat-pipeline"
            label="Pipeline abierto"
            value={formatMXN(stats.openPipeline)}
            icon={<DollarSign className="text-sky-400" size={18} />}
          />
          <StatCard
            testId="stat-won"
            label="Ingresos ganados"
            value={formatMXN(stats.wonRevenue)}
            icon={<DollarSign className="text-emerald-400" size={18} />}
          />
        </section>

        {/* Tabs */}
        <div className="mt-10 flex gap-2 border-b border-slate-800">
          <TabButton
            active={tab === "quotes"}
            onClick={() => setTab("quotes")}
            testId="tab-quotes"
            label="Cotizaciones"
            count={quotes.length}
          />
          <TabButton
            active={tab === "bookings"}
            onClick={() => setTab("bookings")}
            testId="tab-bookings"
            label="Auditorías"
            count={bookings.length}
          />
          <TabButton
            active={tab === "messages"}
            onClick={() => setTab("messages")}
            testId="tab-messages"
            label="Reclutadores"
            count={messages.length}
          />
        </div>

        {/* Filters */}
        <div className="mt-6 flex flex-wrap items-center gap-3 justify-between">
          <div className="flex flex-wrap gap-2" data-testid="status-filters">
            <FilterPill
              active={statusFilter === "all"}
              onClick={() => setStatusFilter("all")}
              testId="filter-all"
              label="Todos"
            />
            {STATUSES.map((s) => (
              <FilterPill
                key={s.id}
                active={statusFilter === s.id}
                onClick={() => setStatusFilter(s.id)}
                testId={`filter-${s.id}`}
                label={s.label}
                styleClass={STATUS_STYLES[s.id]}
              />
            ))}
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="search-input"
              placeholder={tab === "quotes" ? "Buscar email o negocio..." : tab === "messages" ? "Buscar nombre, email o empresa..." : "Buscar nombre o teléfono..."}
              className="pl-9 pr-4 py-2 text-sm rounded-lg bg-slate-900 border border-slate-800 text-white placeholder:text-slate-500 focus:outline-none focus:border-sky-500/60 w-72"
            />
          </div>
        </div>

        {loading ? (
          <div className="py-20 flex items-center justify-center">
            <Loader2 size={26} className="animate-spin text-sky-400" />
          </div>
        ) : tab === "quotes" ? (
          <QuotesTable quotes={filteredQuotes} onUpdateStatus={updateQuoteStatus} />
        ) : tab === "messages" ? (
          <MessagesTable messages={filteredMessages} onUpdateStatus={updateMessageStatus} />
        ) : (
          <BookingsTable bookings={filteredBookings} onUpdateStatus={updateBookingStatus} />
        )}
      </main>
    </div>
  );
}

function StatCard({ label, value, icon, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-xl border border-slate-800 bg-slate-900/60 p-5"
    >
      <div className="flex items-center justify-between">
        <span className="font-mono text-[11px] text-slate-500">// {label}</span>
        {icon}
      </div>
      <div className="mt-2 text-2xl font-semibold text-white font-mono">
        {value}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, testId, label, count }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-5 py-3 text-sm font-medium relative transition-colors ${
        active ? "text-white" : "text-slate-400 hover:text-slate-200"
      }`}
    >
      {label}
      <span
        className={`ml-2 px-1.5 py-0.5 text-[10px] font-mono rounded ${
          active ? "bg-orange-500/20 text-orange-400" : "bg-slate-800 text-slate-500"
        }`}
      >
        {count}
      </span>
      {active && (
        <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-orange-500" />
      )}
    </button>
  );
}

function FilterPill({ active, onClick, testId, label, styleClass }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-all ${
        active
          ? styleClass || "bg-orange-500 text-white border-orange-500"
          : "bg-slate-900 text-slate-400 border-slate-800 hover:border-slate-700"
      }`}
    >
      {label}
    </button>
  );
}

function StatusDropdown({ currentStatus, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const current = STATUSES.find((s) => s.id === (currentStatus || "new")) || STATUSES[0];

  return (
    <div className="relative inline-block" data-testid={testId}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        data-testid={`${testId}-button`}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border ${STATUS_STYLES[current.id]} hover:opacity-80 transition-opacity`}
      >
        {current.label}
        <ChevronDown size={12} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-36 rounded-lg border border-slate-800 bg-slate-900 shadow-xl z-20"
          data-testid={`${testId}-menu`}
        >
          {STATUSES.map((s) => (
            <button
              key={s.id}
              onClick={() => {
                setOpen(false);
                if (s.id !== current.id) onChange(s.id);
              }}
              data-testid={`${testId}-option-${s.id}`}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-800 first:rounded-t-lg last:rounded-b-lg flex items-center justify-between ${
                s.id === current.id ? "text-orange-400" : "text-slate-300"
              }`}
            >
              <span>{s.label}</span>
              {s.id === current.id && <span className="text-orange-400">●</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function QuotesTable({ quotes, onUpdateStatus }) {
  if (quotes.length === 0) {
    return <EmptyState message="No hay cotizaciones que coincidan con tus filtros." />;
  }
  return (
    <div className="mt-6 overflow-visible rounded-xl border border-slate-800">
      <div className="overflow-x-auto overflow-y-visible">
        <table className="w-full text-sm" data-testid="quotes-table">
          <thead className="bg-slate-900/80 text-left">
            <tr className="font-mono text-xs text-slate-500">
              <th className="px-4 py-3">Fecha</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Negocio</th>
              <th className="px-4 py-3">Tipo</th>
              <th className="px-4 py-3">Extras</th>
              <th className="px-4 py-3 text-right">Total</th>
              <th className="px-4 py-3 text-center">Email</th>
              <th className="px-4 py-3 text-right">Estado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-900">
            {quotes.map((q) => (
              <tr
                key={q.id}
                data-testid={`quote-row-${q.id}`}
                className="hover:bg-slate-900/40"
              >
                <td className="px-4 py-3 text-slate-400 font-mono text-xs whitespace-nowrap">
                  {formatDate(q.created_at)}
                </td>
                <td className="px-4 py-3">
                  <a
                    href={`mailto:${q.email}`}
                    className="text-sky-400 hover:text-sky-300 inline-flex items-center gap-1.5"
                  >
                    <Mail size={13} /> {q.email}
                  </a>
                </td>
                <td className="px-4 py-3 text-slate-300">{q.company || "—"}</td>
                <td className="px-4 py-3">
                  <span className="px-2 py-1 rounded bg-slate-800 text-slate-200 text-xs">
                    {SITE_TYPE_LABELS[q.site_type] || q.site_type}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {q.features?.length
                      ? q.features.map((f) => (
                          <span
                            key={f}
                            className="px-1.5 py-0.5 text-[10px] rounded bg-sky-500/10 text-sky-300 border border-sky-500/20"
                          >
                            {FEATURE_LABELS[f] || f}
                          </span>
                        ))
                      : <span className="text-slate-600 text-xs">—</span>}
                  </div>
                </td>
                <td className="px-4 py-3 text-right font-mono font-semibold text-orange-400">
                  {formatMXN(q.total)}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-1.5" title="prospecto / admin">
                    {q.email_sent_to_prospect ? (
                      <CheckCircle2 size={14} className="text-emerald-400" />
                    ) : (
                      <XCircle size={14} className="text-slate-600" />
                    )}
                    {q.email_sent_to_admin ? (
                      <CheckCircle2 size={14} className="text-emerald-400" />
                    ) : (
                      <XCircle size={14} className="text-slate-600" />
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <StatusDropdown
                    currentStatus={q.status}
                    onChange={(s) => onUpdateStatus(q.id, s)}
                    testId={`quote-status-${q.id}`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BookingsTable({ bookings, onUpdateStatus }) {
  if (bookings.length === 0) {
    return <EmptyState message="No hay auditorías que coincidan con tus filtros." />;
  }
  return (
    <div className="mt-6 overflow-visible rounded-xl border border-slate-800">
      <div className="overflow-x-auto overflow-y-visible">
        <table className="w-full text-sm" data-testid="bookings-table">
          <thead className="bg-slate-900/80 text-left">
            <tr className="font-mono text-xs text-slate-500">
              <th className="px-4 py-3">Fecha</th>
              <th className="px-4 py-3">Nombre</th>
              <th className="px-4 py-3">Teléfono</th>
              <th className="px-4 py-3">Notas</th>
              <th className="px-4 py-3 text-right">Estado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-900">
            {bookings.map((b) => (
              <tr
                key={b.id}
                data-testid={`booking-row-${b.id}`}
                className="hover:bg-slate-900/40"
              >
                <td className="px-4 py-3 text-slate-400 font-mono text-xs whitespace-nowrap">
                  {formatDate(b.created_at)}
                </td>
                <td className="px-4 py-3 text-white font-medium">{b.name}</td>
                <td className="px-4 py-3">
                  <a
                    href={`tel:${b.phone}`}
                    className="text-sky-400 hover:text-sky-300 inline-flex items-center gap-1.5"
                  >
                    <Phone size={13} /> {b.phone}
                  </a>
                </td>
                <td className="px-4 py-3 text-slate-300 max-w-md truncate">
                  {b.notes || "—"}
                </td>
                <td className="px-4 py-3 text-right">
                  <StatusDropdown
                    currentStatus={b.status}
                    onChange={(s) => onUpdateStatus(b.id, s)}
                    testId={`booking-status-${b.id}`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const OPPORTUNITY_LABELS = { job: "Empleo", freelance: "Freelance", consulting: "Consultoría" };

function MessagesTable({ messages, onUpdateStatus }) {
  if (messages.length === 0) {
    return <EmptyState message="No hay mensajes de reclutadores que coincidan con tus filtros." />;
  }
  return (
    <div className="mt-6 overflow-visible rounded-xl border border-slate-800">
      <div className="overflow-x-auto overflow-y-visible">
        <table className="w-full text-sm" data-testid="messages-table">
          <thead className="bg-slate-900/80 text-left">
            <tr className="font-mono text-xs text-slate-500">
              <th className="px-4 py-3">Fecha</th>
              <th className="px-4 py-3">Nombre</th>
              <th className="px-4 py-3">Empresa</th>
              <th className="px-4 py-3">Tipo</th>
              <th className="px-4 py-3">Mensaje</th>
              <th className="px-4 py-3 text-right">Estado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-900">
            {messages.map((m) => (
              <tr key={m.id} data-testid={`message-row-${m.id}`} className="hover:bg-slate-900/40 align-top">
                <td className="px-4 py-3 text-slate-400 font-mono text-xs whitespace-nowrap">{formatDate(m.created_at)}</td>
                <td className="px-4 py-3">
                  <div className="text-white font-medium">{m.name}</div>
                  <a href={`mailto:${m.email}`} className="text-sky-400 hover:text-sky-300 inline-flex items-center gap-1.5 text-xs">
                    <Mail size={12} /> {m.email}
                  </a>
                </td>
                <td className="px-4 py-3 text-slate-300">{m.company || "—"}</td>
                <td className="px-4 py-3 text-slate-300">{OPPORTUNITY_LABELS[m.opportunity] || "—"}</td>
                <td className="px-4 py-3 text-slate-300 max-w-md whitespace-pre-wrap">{m.message}</td>
                <td className="px-4 py-3 text-right">
                  <StatusDropdown currentStatus={m.status} onChange={(s) => onUpdateStatus(m.id, s)} testId={`message-status-${m.id}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div
      data-testid="empty-state"
      className="mt-6 rounded-xl border border-dashed border-slate-800 bg-slate-900/40 p-12 text-center"
    >
      <Inbox size={32} className="mx-auto text-slate-600 mb-3" />
      <p className="text-slate-400">{message}</p>
      <p className="text-slate-600 font-mono text-xs mt-1">// listo cuando lleguen</p>
    </div>
  );
}
